// src/services/reviewApi.ts
import axios from 'axios';
import { setupAxiosInterceptors } from '../utils/axiosSetup';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  withCredentials: true, // CRITICAL: Send session cookies with all requests
});

// Set up standard interceptors (includes 401 handling and session management)
setupAxiosInterceptors(apiClient);

export interface AcknowledgeReviewRequest {
  comment?: string;
  actionId?: number; // Optional: can be included in body for execution view access
}

export interface ReviewActionDetails {
  actionId: number;
  actionType: string;
  documentName?: string;
  projectName?: string;
  phaseName?: string;
  phaseDescription?: string;
  taskName?: string;
  taskDescription?: string;
  taskId?: number;
  projectId?: number;
  spItemId?: number;
}

export interface AcknowledgeReviewResponse {
  success: boolean;
  message: string;
  actionId: number;
  actionType: string;
  status: string;
  actualEndDate: string;
  kpiScore: number;
  comment?: string;
}

export interface ReviewApiError {
  error: boolean;
  message: string;
  code?: 'INVALID_TOKEN' | 'TOKEN_EXPIRED' | 'ACTION_ALREADY_COMPLETE' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'MISSING_TOKEN' | 'ACTION_NOT_FOUND' | 'NOT_ASSIGNED' | 'UNAUTHORIZED';
}

/**
 * Get review action details from token or actionId (optional - for displaying document/project/task info)
 * @param token - JWT token from URL query parameter (optional if actionId is provided)
 * @param actionId - Action ID for execution view access (optional if token is provided)
 * @returns Action details including document, project, and task information
 * @throws ReviewApiError with appropriate error code
 */
export async function getReviewActionDetails(token: string | null, actionId?: number | null): Promise<ReviewActionDetails> {
  try {
    // Use actionId-based access if token is not provided
    const url = token 
      ? `/api/review/details?token=${encodeURIComponent(token)}`
      : actionId 
        ? `/api/review/details?actionId=${actionId}`
        : '/api/review/details';
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    // Handle specific error codes from backend
    if (error.response?.status === 400) {
      const errorData = error.response.data;
      if (errorData.code === 'MISSING_TOKEN') {
        // Missing token is a validation error, but we'll return empty details
        // since the form can still work without displaying details
        console.warn('Missing token parameter:', errorData.message);
        return {
          actionId: 0,
          actionType: 'REVIEW',
        };
      }
      if (errorData.code === 'INVALID_TOKEN') {
        // Invalid token - return empty details but don't block form
        console.warn('Invalid token:', errorData.message);
        return {
          actionId: 0,
          actionType: 'REVIEW',
        };
      }
      if (errorData.code === 'TOKEN_EXPIRED') {
        // Expired token - return empty details but don't block form
        console.warn('Expired token:', errorData.message);
        return {
          actionId: 0,
          actionType: 'REVIEW',
        };
      }
      // Other 400 errors - return empty details
      console.warn('Failed to fetch action details:', errorData.message);
      return {
        actionId: 0,
        actionType: 'REVIEW',
      };
    }
    
    if (error.response?.status === 404) {
      const errorData = error.response.data;
      if (errorData.code === 'ACTION_NOT_FOUND') {
        // Action not found - return empty details but don't block form
        console.warn('Action not found:', errorData.message);
        return {
          actionId: 0,
          actionType: 'REVIEW',
        };
      }
      // Other 404 errors
      console.warn('Resource not found:', errorData?.message || 'Action details not found');
      return {
        actionId: 0,
        actionType: 'REVIEW',
      };
    }
    
    if (error.response?.status === 401) {
      // Authentication error - return empty details but don't block form
      console.warn('Authentication required for action details');
      return {
        actionId: 0,
        actionType: 'REVIEW',
      };
    }

    // Network or other errors - return empty details rather than throwing
    // This allows the form to still work even if details can't be fetched
    console.warn('Could not fetch action details:', error.message);
    return {
      actionId: 0,
      actionType: 'REVIEW',
    };
  }
}

/**
 * Acknowledge a review action with optional comment
 * @param token - JWT token from URL query parameter (optional if actionId is provided)
 * @param data - Request body with optional comment
 * @param actionId - Action ID for execution view access (optional if token is provided)
 * @returns Response with action details
 * @throws ReviewApiError with appropriate error code
 */
export async function acknowledgeReview(
  token: string | null,
  data: AcknowledgeReviewRequest,
  actionId?: number | null
): Promise<AcknowledgeReviewResponse> {
  try {
    // Use actionId-based authentication if token is not provided
    // Backend summary says: "Query param or body: actionId" - so it accepts either
    // We'll use query string as per backend summary example, but also include in body for compatibility
    const url = token 
      ? `/api/review/acknowledge?token=${encodeURIComponent(token)}`
      : actionId 
        ? `/api/review/acknowledge?actionId=${actionId}`
        : '/api/review/acknowledge';
    
    // For execution view (actionId without token), include actionId in body as well
    // Some backends may prefer body over query string for POST requests
    const requestData = actionId && !token 
      ? { ...data, actionId }
      : data;
    
    console.log('[Review API] Acknowledging review:', {
      url,
      method: 'POST',
      hasToken: !!token,
      actionId: actionId || null,
      actionIdInQuery: !!(actionId && !token),
      actionIdInBody: !!(actionId && !token),
      withCredentials: true,
      requestData,
      cookiesPresent: document.cookie ? 'Yes' : 'No',
    });
    
    // Ensure Content-Type header is set for JSON and credentials are sent.
    // Use longer timeout (60s): completion + recalc can exceed default 30s on larger projects.
    const response = await apiClient.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Explicitly ensure credentials are sent
      timeout: 60000,
    });
    console.log('[Review API] Acknowledgment successful:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('[Review API] Acknowledgment error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      hasCredentials: error.config?.withCredentials,
    });
    
    // Handle specific error codes from backend
    if (error.response?.status === 400) {
      const errorData = error.response.data;
      if (errorData.code === 'INVALID_TOKEN') {
        throw {
          error: true,
          message: errorData.message || 'Invalid token. Please check your link and try again.',
          code: 'INVALID_TOKEN',
        } as ReviewApiError;
      }
      if (errorData.code === 'TOKEN_EXPIRED') {
        throw {
          error: true,
          message: errorData.message || 'This link has expired. Please request a new review link.',
          code: 'TOKEN_EXPIRED',
        } as ReviewApiError;
      }
      if (errorData.code === 'ACTION_ALREADY_COMPLETE') {
        throw {
          error: true,
          message: errorData.message || 'This review action has already been completed.',
          code: 'ACTION_ALREADY_COMPLETE',
        } as ReviewApiError;
      }
      throw {
        error: true,
        message: errorData.message || 'Validation error. Please check your input and try again.',
        code: 'VALIDATION_ERROR',
      } as ReviewApiError;
    }
    
    if (error.response?.status === 401) {
      const errorData = error.response.data;
      // Use backend's error message if available, otherwise use default
      const errorMessage = errorData?.message || errorData?.error || 'Authentication required. Please log in and try again.';
      throw {
        error: true,
        message: errorMessage,
        code: 'UNAUTHORIZED',
      } as ReviewApiError;
    }
    
    if (error.response?.status === 403) {
      const errorData = error.response.data;
      throw {
        error: true,
        message: errorData.message || 'You are not assigned to this action. Only assigned users can acknowledge review actions.',
        code: 'NOT_ASSIGNED',
      } as ReviewApiError;
    }

    // Network or other errors
    throw {
      error: true,
      message: error.message || 'Failed to acknowledge review. Please try again later.',
      code: 'NETWORK_ERROR',
    } as ReviewApiError;
  }
}

